import type {
  BookingStop,
  StopNumber,
  StopTimeObject,
} from '@/models/advancedJobBooking';

import { format } from 'date-fns';

import {
  BOOKING_AM_WINDOW,
  BOOKING_DAY_END_MIN,
  BOOKING_DAY_START_MIN,
  BOOKING_PM_WINDOW,
  BOOKING_WAREHOUSE_DAY_START_MIN,
} from '@/utils/PortalConstants';

// Timeline of stop windows on a working-day axis (9 AM–5 PM), expanded to fit
// out-of-range times. Each stop is blocked until 30 min after the previous one.
const STOP_GAP_MIN = 30;
const MIN_STOP_WINDOW_MIN = 30;

const AM_WINDOW = {
  start: BOOKING_AM_WINDOW.startMin,
  end: BOOKING_AM_WINDOW.endMin,
};
const PM_WINDOW = {
  start: BOOKING_PM_WINDOW.startMin,
  end: BOOKING_PM_WINDOW.endMin,
};

export interface TimelineBar {
  number: StopNumber;
  label: string;
  role: 'Pickup window' | 'Delivery window';
  leftPct: number;
  widthPct: number;
}

export interface TimelineBlocked {
  number: StopNumber;
  leftPct: number;
  widthPct: number;
}

export interface StopsTimeline {
  rangeLabel: string;
  ticks: string[];
  bars: TimelineBar[];
  blocked: TimelineBlocked[];
}

/** Convert a time-of-day object to minutes since midnight. */
export function timeToMinutes(time: StopTimeObject): number {
  return time.hours * 60 + time.minutes;
}

/** Convert minutes since midnight back to a time-of-day object. */
export function minutesToStopTime(min: number): StopTimeObject {
  return { hours: Math.floor(min / 60), minutes: min % 60 };
}

/** Add the inter-stop gap (30 min) to a time. */
export function addStopGap(time: StopTimeObject): StopTimeObject {
  return minutesToStopTime(timeToMinutes(time) + STOP_GAP_MIN);
}

// Time-of-day Date (date part irrelevant) so date-fns handles 12h/AM-PM format.
function timeOfDayDate(hours: number, minutes = 0): Date {
  const normalizedHours = ((hours % 24) + 24) % 24;
  return new Date(2000, 0, 1, normalizedHours, minutes);
}

/** Format a whole hour as a short label, e.g. `9AM`, `12PM`. */
function formatHour(hour: number): string {
  return format(timeOfDayDate(hour), 'ha');
}

/** Format a stop time as a 12-hour label, e.g. `9:30 AM`. */
export function formatStopTime(time: StopTimeObject): string {
  return format(timeOfDayDate(time.hours, time.minutes), 'h:mm a');
}

function windowForStop(stop: BookingStop): { start: number; end: number } {
  if (stop.time_mode === 'specific') {
    return {
      start: stop.time_from
        ? timeToMinutes(stop.time_from)
        : BOOKING_DAY_START_MIN,
      end: stop.time_to ? timeToMinutes(stop.time_to) : BOOKING_DAY_END_MIN,
    };
  }
  if (stop.time_mode === 'ampm') {
    if (stop.am_pm === 'AM') return { ...AM_WINDOW };
    if (stop.am_pm === 'PM') return { ...PM_WINDOW };
  }
  return {
    start: stop.warehouse
      ? BOOKING_WAREHOUSE_DAY_START_MIN
      : BOOKING_DAY_START_MIN,
    end: BOOKING_DAY_END_MIN,
  };
}

/**
 * True when a specific time window is narrower than 3 hours, which incurs a
 * Priority Service charge. Only applies to specific mode stops.
 */
export function stopIsPriority(stop: BookingStop): boolean {
  if (stop.time_mode !== 'specific' || !stop.time_from || !stop.time_to) {
    return false;
  }
  return timeToMinutes(stop.time_to) - timeToMinutes(stop.time_from) < 180;
}

/** Effective start (minutes) per stop: ≥30 min after the previous stop's start.
 * If the previous stop has no specific time (mode=any), no gap is enforced. */
function effectiveStartsMin(stops: BookingStop[]): number[] {
  const result: number[] = [];
  let prevStart: number | null = null;
  let prevIsAny = false;
  for (const stop of stops) {
    const window = windowForStop(stop);
    const earliestAllowed =
      prevStart === null || prevIsAny ? window.start : prevStart + STOP_GAP_MIN;
    const start = Math.max(window.start, earliestAllowed);
    result.push(start);
    prevStart = start;
    prevIsAny = stop.time_mode === 'any';
  }
  return result;
}

/**
 * Min time for a stop's picker — the previous stop's effective start. The picker
 * disables slots up to it, so the earliest selectable is 30 min later. Null for
 * the first stop.
 */
export function stopMinTime(
  stops: BookingStop[],
  index: number,
): StopTimeObject | null {
  if (index <= 0) return null;
  if (stops[index - 1].time_mode === 'any') return null;
  return minutesToStopTime(effectiveStartsMin(stops)[index - 1]);
}

/** Earliest a stop can be booked: the inter-stop gap after its min time; null for the first. */
export function stopAvailableFrom(
  stops: BookingStop[],
  index: number,
): StopTimeObject | null {
  const minTime = stopMinTime(stops, index);
  return minTime ? addStopGap(minTime) : null;
}

/**
 * Resolve every stop's window so it is consistent with the inter-stop gap.
 */
export function clampStopsToMinTimes(stops: BookingStop[]): BookingStop[] {
  let prevStart: number | null = null;
  let prevIsAny = false;

  return stops.map((stop) => {
    const window = windowForStop(stop);
    const earliestAllowed =
      prevStart === null || prevIsAny ? window.start : prevStart + STOP_GAP_MIN;

    let next = stop;
    let resolvedStart = Math.max(window.start, earliestAllowed);

    if (stop.time_mode === 'specific') {
      const origFrom = stop.time_from ? timeToMinutes(stop.time_from) : null;
      const origTo = stop.time_to ? timeToMinutes(stop.time_to) : null;
      // Raise the start to the earliest available slot (defaulting an unset one).
      const fromMin =
        origFrom !== null && origFrom >= earliestAllowed
          ? origFrom
          : earliestAllowed;
      // Default an unset end to the end of day, then keep the window ≥ minimum.
      const toMin = Math.max(
        origTo ?? BOOKING_DAY_END_MIN,
        fromMin + MIN_STOP_WINDOW_MIN,
      );
      resolvedStart = fromMin;
      if (fromMin !== origFrom || toMin !== origTo) {
        next = {
          ...stop,
          time_from: minutesToStopTime(fromMin),
          time_to: minutesToStopTime(toMin),
        };
      }
    } else if (stop.time_mode === 'ampm') {
      // Shift the start by the gap. Keep "AM/PM" while the window still fits the
      // preset; otherwise demote to "Custom".
      const fromMin = Math.max(window.start, earliestAllowed);
      const fitsPreset = fromMin + MIN_STOP_WINDOW_MIN <= window.end;
      const mode = fitsPreset ? 'ampm' : 'specific';
      const amPm = fitsPreset ? stop.am_pm : null;
      const toMin = fitsPreset
        ? window.end
        : Math.max(BOOKING_DAY_END_MIN, fromMin + MIN_STOP_WINDOW_MIN);
      const origFrom = stop.time_from ? timeToMinutes(stop.time_from) : null;
      const origTo = stop.time_to ? timeToMinutes(stop.time_to) : null;
      resolvedStart = fromMin;
      if (
        mode !== stop.time_mode ||
        amPm !== stop.am_pm ||
        fromMin !== origFrom ||
        toMin !== origTo
      ) {
        next = {
          ...stop,
          time_mode: mode,
          am_pm: amPm,
          time_from: minutesToStopTime(fromMin),
          time_to: minutesToStopTime(toMin),
        };
      }
    }

    prevStart = resolvedStart;
    prevIsAny = stop.time_mode === 'any';
    return next;
  });
}

export function reclampDraftStops(
  stops: BookingStop[],
  minDate: string,
): BookingStop[] {
  return clampStopsToMinTimes(
    stops.map((stop) => {
      const startDate =
        stop.start_date && stop.start_date >= minDate
          ? stop.start_date
          : minDate;
      const endDate = stop.link_dates
        ? startDate
        : stop.end_date && stop.end_date >= minDate
          ? stop.end_date
          : null;
      return { ...stop, start_date: startDate, end_date: endDate };
    }),
  );
}

/** True when two adjacent stops share the same address name. */
export function hasDuplicateConsecutiveStops(stops: BookingStop[]): boolean {
  return stops.some((stop, index) => {
    if (index >= stops.length - 1 || !stop.name) return false;
    return stop.name === stops[index + 1].name;
  });
}

export function buildStopsTimeline(stops: BookingStop[]): StopsTimeline {
  const effStarts = effectiveStartsMin(stops);

  // Axis defaults to the working day but expands to fit any out-of-range time.
  let minMin = BOOKING_DAY_START_MIN;
  let maxMin = BOOKING_DAY_END_MIN;
  stops.forEach((stop, index) => {
    const window = windowForStop(stop);
    minMin = Math.min(minMin, effStarts[index], window.start);
    maxMin = Math.max(maxMin, window.end);
  });

  const startHour = Math.floor(minMin / 60);
  const endHour = Math.ceil(maxMin / 60);
  const rangeStart = startHour * 60;
  const span = Math.max(1, endHour * 60 - rangeStart);
  const toPct = (min: number): number =>
    ((Math.min(Math.max(min, rangeStart), endHour * 60) - rangeStart) / span) *
    100;

  const ticks: string[] = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    ticks.push(formatHour(hour));
  }

  const bars: TimelineBar[] = stops.map((stop, index) => {
    const window = windowForStop(stop);
    const leftPct = toPct(effStarts[index]);
    return {
      number: stop.number,
      label: `Stop ${stop.number}`,
      role: stop.number === 1 ? 'Pickup window' : 'Delivery window',
      leftPct,
      widthPct: Math.max(0, toPct(window.end) - leftPct),
    };
  });

  const blocked: TimelineBlocked[] = stops.slice(1).map((stop, prevIndex) => ({
    number: stop.number,
    leftPct: 0,
    widthPct: toPct(effStarts[prevIndex] + STOP_GAP_MIN),
  }));

  return {
    rangeLabel: `${formatHour(startHour)} - ${formatHour(endHour)}`,
    ticks,
    bars,
    blocked,
  };
}
